import { useCallback, useState } from 'react';
import {
  useMaintenanceSubmit,
  MaintenanceSubmitArgs,
  MaintenanceSubmitResult,
} from '@/hooks/residents/useMaintenanceSubmit';

export type SubmitPhase = 'form' | 'submitting' | 'success';

/**
 * Floor on the "submitting" state so a fast optimistic submit doesn't flash past
 * before the resident registers it.
 */
export const MIN_SUBMITTING_MS = 600;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const useMaintenanceSubmitFlow = () => {
  const { submitTicket } = useMaintenanceSubmit();
  const [phase, setPhase] = useState<SubmitPhase>('form');

  const submit = useCallback(
    async (args: MaintenanceSubmitArgs): Promise<MaintenanceSubmitResult> => {
      setPhase('submitting');
      const start = Date.now();
      const result = await submitTicket(args);
      const elapsed = Date.now() - start;
      if (result.success && elapsed < MIN_SUBMITTING_MS) {
        await delay(MIN_SUBMITTING_MS - elapsed);
      }
      setPhase(result.success ? 'success' : 'form');
      return result;
    },
    [submitTicket],
  );

  const reset = useCallback(() => setPhase('form'), []);

  return { phase, submit, reset };
};
